import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { API_URL, getToken, post, get, del } from "@/lib/api";

/**
 * Global assistant state: one conversation, three surfaces (dock · split · full) and page context.
 * Any component can `useAssistant().ask("…", { project_id })` – the surface opens with the answer streaming in.
 */
const Ctx = createContext(null);
const MODE_KEY = "si_assistant_mode";
const WIDTH_KEY = "si_assistant_split";

const routeContext = (pathname, search) => {
  const m = pathname.match(/^\/(prosjekter|moduler|kunder|henvendelser|tilbud)\/([^/]+)/);
  const map = { prosjekter: "project_id", moduler: "module_id", kunder: "customer_id", henvendelser: "lead_id", tilbud: "quote_id" };
  const ctx = { path: pathname };
  if (m && m[2] !== "ny") ctx[map[m[1]]] = m[2];
  const f = pathname.match(/^\/okonomi\/(fakturaer|kontrakter|prosjekter)\/([^/]+)/);
  if (f) ctx[{ fakturaer: "invoice_id", kontrakter: "contract_id", prosjekter: "project_id" }[f[1]]] = f[2];
  const sp = new URLSearchParams(search);
  if (sp.get("lokasjon")) ctx.location_id = sp.get("lokasjon");
  if (sp.get("dato")) ctx.date = sp.get("dato");
  if (sp.get("plan") && pathname.startsWith("/planlegging/kapasitet")) ctx.plan_id = sp.get("plan");
  if (sp.get("tab") && m?.[1] === "prosjekter") ctx.tab = sp.get("tab");
  return ctx;
};

const newSessionId = () => `s-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;

export const AssistantProvider = ({ children }) => {
  const location = useLocation();
  const [mode, setModeState] = useState(() => {
    try {
      const m = localStorage.getItem(MODE_KEY);
      return m === "split" ? "split" : "closed"; // dock/full never persist as open
    } catch {
      return "closed";
    }
  });
  const [sessionId, setSessionId] = useState(newSessionId);
  const [messages, setMessages] = useState([]);
  const [streaming, setStreaming] = useState(false);
  const [progress, setProgress] = useState(null); // { stage, tools: [{id,name,label,done,ok,summary}] }
  const [extraContext, setExtraContext] = useState(null); // e.g. map selection set by pages
  const [splitSize, setSplitSizeState] = useState(() => {
    try {
      return Number(localStorage.getItem(WIDTH_KEY)) || 42;
    } catch {
      return 42;
    }
  });
  const abortRef = useRef(null);
  const draftRef = useRef("");
  const [draft, setDraft] = useState("");

  const pageContext = useMemo(() => ({ ...routeContext(location.pathname, location.search), ...(extraContext || {}) }), [location.pathname, location.search, extraContext]);

  const setMode = useCallback((m) => {
    setModeState(m);
    try {
      if (m === "split") localStorage.setItem(MODE_KEY, "split");
      else localStorage.removeItem(MODE_KEY);
    } catch {
      /* ignore */
    }
  }, []);
  const setSplitSize = useCallback((n) => {
    setSplitSizeState(n);
    try {
      localStorage.setItem(WIDTH_KEY, String(n));
    } catch {
      /* ignore */
    }
  }, []);

  const open = useCallback((m) => setMode(m || (mode === "closed" ? "dock" : mode)), [mode, setMode]);
  const close = useCallback(() => setMode("closed"), [setMode]);
  const toggle = useCallback(() => setMode(mode === "closed" ? "dock" : "closed"), [mode, setMode]);

  const stop = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setStreaming(false);
    setProgress(null);
  }, []);

  const newSession = useCallback(() => {
    stop();
    setSessionId(newSessionId());
    setMessages([]);
  }, [stop]);

  const loadSession = useCallback(
    async (id) => {
      stop();
      const msgs = await get(`/ai/sessions/${id}`);
      setSessionId(id);
      setMessages(msgs.map((m) => ({ ...m, done: true })));
    },
    [stop]
  );

  const deleteSession = useCallback(
    async (id) => {
      await del(`/ai/sessions/${id}`);
      if (id === sessionId) newSession();
    },
    [sessionId, newSession]
  );

  /** Send a message; streams the answer into the last assistant message. */
  const ask = useCallback(
    async (text, ctxOverride) => {
      const msg = (text || "").trim();
      if (!msg || streaming) return;
      if (mode === "closed") setMode("dock");
      const context = { ...pageContext, ...(ctxOverride || {}) };
      const userMsg = { id: `u-${Date.now()}`, role: "user", content: msg, created_at: new Date().toISOString(), done: true };
      const asstId = `a-${Date.now()}`;
      setMessages((m) => [...m, userMsg, { id: asstId, role: "assistant", content: "", widgets: [], sources: [], actions: [], done: false }]);
      setStreaming(true);
      setProgress({ stage: "thinking", tools: [] });
      const ctrl = new AbortController();
      abortRef.current = ctrl;
      try {
        const res = await fetch(`${API_URL}/ai/chat`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken() || ""}` },
          body: JSON.stringify({ message: msg, session_id: sessionId, context }),
          signal: ctrl.signal,
        });
        if (!res.ok || !res.body) {
          let detail = "AI-tjenesten svarte ikke";
          try {
            detail = (await res.json()).detail || detail;
          } catch {
            /* ignore */
          }
          throw new Error(detail);
        }
        const reader = res.body.getReader();
        const dec = new TextDecoder();
        let buf = "";
        const handle = (ev) => {
          if (ev.type === "delta") {
            setMessages((m) => m.map((x) => (x.id === asstId ? { ...x, content: x.content + ev.text } : x)));
          } else if (ev.type === "status") {
            setProgress((p) => ({ ...(p || { tools: [] }), stage: ev.stage }));
          } else if (ev.type === "tool_start") {
            setProgress((p) => ({ stage: "tools", tools: [...(p?.tools || []), { id: ev.id, name: ev.name, label: ev.label, done: false }] }));
          } else if (ev.type === "tool_done") {
            setProgress((p) => ({ ...(p || {}), tools: (p?.tools || []).map((t) => (t.id === ev.id ? { ...t, done: true, ok: ev.ok, summary: ev.summary } : t)) }));
          } else if (ev.type === "done") {
            setMessages((m) => m.map((x) => (x.id === asstId ? { ...ev.message, id: asstId, server_id: ev.message.id, done: true } : x)));
          } else if (ev.type === "error") {
            setMessages((m) => m.map((x) => (x.id === asstId ? { ...x, content: x.content || ev.message, error: true, done: true } : x)));
          }
        };
        while (true) {
          const { value, done } = await reader.read();
          if (done) break;
          buf += dec.decode(value, { stream: true });
          let idx;
          while ((idx = buf.indexOf("\n\n")) !== -1) {
            const chunk = buf.slice(0, idx);
            buf = buf.slice(idx + 2);
            for (const line of chunk.split("\n")) {
              if (!line.startsWith("data:")) continue;
              try {
                handle(JSON.parse(line.slice(5)));
              } catch {
                /* partial */
              }
            }
          }
        }
      } catch (e) {
        if (e.name !== "AbortError") {
          setMessages((m) => m.map((x) => (x.id === asstId ? { ...x, content: x.content || `Beklager – ${e.message || "noe gikk galt"}.`, error: true, done: true } : x)));
        }
      } finally {
        setMessages((m) => m.map((x) => (x.id === asstId && !x.done ? { ...x, done: true } : x)));
        setStreaming(false);
        setProgress(null);
        abortRef.current = null;
      }
    },
    [mode, pageContext, sessionId, streaming, setMode]
  );

  const updateAction = useCallback((actionId, patch) => {
    setMessages((m) => m.map((x) => (x.actions?.some((a) => a.id === actionId) ? { ...x, actions: x.actions.map((a) => (a.id === actionId ? { ...a, ...patch } : a)) } : x)));
  }, []);

  const confirmAction = useCallback(
    async (actionId) => {
      updateAction(actionId, { busy: true });
      try {
        const r = await post(`/ai/actions/${actionId}/confirm`);
        updateAction(actionId, { ...r, busy: false });
        return r;
      } catch (e) {
        updateAction(actionId, { busy: false, status: e?.response?.status === 409 ? "confirmed" : "failed", error: e?.response?.data?.detail?.message || e?.response?.data?.detail || e.message });
        throw e;
      }
    },
    [updateAction]
  );

  const rejectAction = useCallback(
    async (actionId) => {
      const r = await post(`/ai/actions/${actionId}/reject`);
      updateAction(actionId, r);
    },
    [updateAction]
  );

  // ⌘J / Ctrl+J toggles the dock; Esc closes it
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "j") {
        e.preventDefault();
        toggle();
      } else if (e.key === "Escape" && mode === "dock") {
        close();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [toggle, close, mode]);

  useEffect(() => {
    draftRef.current = draft;
  }, [draft]);

  const value = useMemo(
    () => ({ mode, setMode, open, close, toggle, sessionId, messages, streaming, progress, ask, stop, newSession, loadSession, deleteSession, confirmAction, rejectAction, pageContext, setExtraContext, splitSize, setSplitSize, draft, setDraft }),
    [mode, setMode, open, close, toggle, sessionId, messages, streaming, progress, ask, stop, newSession, loadSession, deleteSession, confirmAction, rejectAction, pageContext, splitSize, setSplitSize, draft]
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};

/** Same as useAssistant but returns null outside the provider (public pages, storybook-ish contexts). */
export const useAssistantOptional = () => useContext(Ctx);

export const useAssistant = () => {
  const v = useContext(Ctx);
  if (!v) throw new Error("useAssistant must be used inside AssistantProvider");
  return v;
};
